import { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink } from "lucide-react";
import { useLang } from "@/i18n";
import { API } from "@/config/site";
import { PageHero, Reveal, CopyButton } from "@/components/common";
import { Skeleton } from "@/components/ui/skeleton";

const ENTRY_KEYS = [
  { key: "mint", labelKey: "trans.mint" },
  { key: "treasury", labelKey: "trans.treasury" },
  { key: "pool", labelKey: "trans.pool" },
  { key: "lock", labelKey: "trans.lock" },
  { key: "collection", labelKey: "trans.collection" },
];

export default function Transparency() {
  const { t } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/transparency`).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  return (
    <>
      <PageHero eyebrow={t("trans.eyebrow")} title={t("trans.title")} sub={t("trans.sub")} />

      <section className="section-pad grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="transparency-grid">
        {ENTRY_KEYS.map((e, i) => {
          const entry = data?.entries?.[e.key];
          return (
            <Reveal key={e.key} delay={i * 0.06}>
              <div className="card-surface h-full p-6" data-testid={`trans-${e.key}`}>
                <div className="flex items-center justify-between">
                  <p className="font-heading text-[11px] font-bold uppercase tracking-[0.25em] text-mist">{t(e.labelKey)}</p>
                  {!data ? (
                    <Skeleton className="h-5 w-24 bg-white/5" />
                  ) : (
                    <span className={`mono-chip ${entry?.status === "DEPLOYED" ? "border-neon/40 text-neon" : "border-gold/40 text-goldlight"}`}>
                      {entry?.status === "DEPLOYED" ? "DEPLOYED" : t("common.notDeployed")}
                    </span>
                  )}
                </div>
                {data && entry?.status !== "DEPLOYED" && (
                  <>
                    <p className="mt-5 font-display text-xl uppercase text-white/25">—</p>
                    <p className="mt-3 text-xs leading-relaxed text-mist">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-gold/80">{t("trans.what")}: </span>
                      {t("trans.missingDeploy")}
                    </p>
                  </>
                )}
                {data && entry?.status === "DEPLOYED" && (
                  <>
                    <p className="mt-5 break-all font-mono text-xs text-paper">{entry.address}</p>
                    <div className="mt-4 flex gap-2">
                      <CopyButton value={entry.address} />
                      <a href={entry.solscan_url} target="_blank" rel="noopener noreferrer" className="mono-chip border-neon/40 text-neon">
                        <ExternalLink size={12} /> {t("common.viewSolscan")}
                      </a>
                    </div>
                  </>
                )}
              </div>
            </Reveal>
          );
        })}

        <Reveal delay={0.3}>
          <div className="card-surface h-full p-6" data-testid="trans-authorities">
            <p className="font-heading text-[11px] font-bold uppercase tracking-[0.25em] text-mist">{t("trans.mintAuth")} / {t("trans.freezeAuth")}</p>
            <p className="mt-5 font-mono text-sm font-bold uppercase tracking-wider text-goldlight">{t("common.notDeployed")}</p>
            <p className="mt-3 text-xs leading-relaxed text-mist">{t("trans.missingDeploy")}</p>
          </div>
        </Reveal>
      </section>

      <section className="section-pad mt-6">
        <Reveal className="card-surface p-7" data-testid="trans-txs">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-paper">{t("trans.txs")}</h2>
            {data?.updated_at && (
              <p className="font-mono text-[10px] uppercase tracking-widest text-mist/70">
                {t("trans.lastUpdate")}: {new Date(data.updated_at).toLocaleString()} · Solana RPC
              </p>
            )}
          </div>
          <div className="mt-6 rounded-xl border border-dashed border-white/10 p-10 text-center text-sm text-mist">
            {t("trans.noTxs")}
          </div>
        </Reveal>
      </section>
    </>
  );
}
